// import { applyAction } from '$app/forms';
import type { SubmitFunction } from '@sveltejs/kit';
import type { onDestroy as svelteOnDestroy } from 'svelte';
import { writable, type Writable, derived } from 'svelte/store';
import type { ZodValidation } from 'sveltekit-superforms';
import { superForm } from 'sveltekit-superforms/client';
import type { AnyZodObject } from 'zod';

const _isSuperformSubmitting: Writable<Record<string, true>> = writable({});
export function _updateSubmittingForms(formId: string, isSubmitting: boolean) {
	return _isSuperformSubmitting.update((v) => {
		const v2 = { ...v };
		if (isSubmitting) {
			v2[formId] = true;
		} else {
			delete v2[formId];
		}
		return v2;
	});
}
export const isSuperformSubmitting = derived(
	_isSuperformSubmitting,
	(v) => Object.keys(v).length > 0
);

/**
 * Wraps a superform() call with handlers that updates the formSubmittionStore
 * so we can tell when any form across the app is being submitted.
 */
export function buildSuperForm<
	T extends ZodValidation<AnyZodObject> = ZodValidation<AnyZodObject>,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	M = any
>(onDestroy: typeof svelteOnDestroy, ...opts: Parameters<typeof superForm<T, M>>) {
	let supaform: ReturnType<typeof superForm<T, M>>;

	// This only makes sense to do in the browser.
	if (typeof window !== 'undefined') {
		const supaformId = (opts[0].id ?? 'n/a') + '-' + Date.now().toString();

		// opts[1] ||= {};
		// const prevOnSubmit = opts[1].onSubmit;
		// opts[1].onSubmit = (...args) => {
		// 	console.log('submitting form ' + supaformId);
		// 	prevOnSubmit?.(...args);
		// };
		// const prevOnError = opts[1].onError;
		// opts[1].onError = (...args) => {
		// 	console.log('error on form ' + supaformId);
		// 	if (prevOnError === 'apply') {
		// 		applyAction(args[0].result);
		// 	} else {
		// 		prevOnError?.(...args);
		// 	}
		// };
		// const prevOnUpdated = opts[1].onUpdated;
		// opts[1].onUpdated = (...args) => {
		// 	console.log('updated form ' + supaformId);
		// 	prevOnUpdated?.(...args);
		// };

		supaform = superForm(...opts);

		const cleanup = () => {
			_updateSubmittingForms(supaformId, false);
		};

		const unsub = supaform.submitting.subscribe((submitting) => {
			_updateSubmittingForms(supaformId, submitting);
		}, cleanup);

		// const unsub2 = supaform.delayed.subscribe((delayed) => {
		// 	console.log('delayed form ' + supaformId, delayed);
		// });

		onDestroy(() => {
			unsub();
			cleanup();
			// unsub2();
		});
	} else {
		supaform = superForm(...opts);
	}

	return supaform;
}

export function enhanceForm(id?: string): SubmitFunction {
	const formUid = (id ?? 'n/a') + '-' + Date.now().toString();

	return () => {
		_updateSubmittingForms(formUid, true);

		return async ({ update }) => {
			try {
				await update();
			} finally {
				_updateSubmittingForms(formUid, false);
			}
		};
	};
}

export function buildManualFormLifecycle(id?: string) {
	const formId = (id ?? 'n/a') + '-' + Date.now().toString();

	let running = false;

	return {
		start: () => {
			if (running) {
				return;
			}
			running = true;

			_updateSubmittingForms(formId, true);
		},
		end: () => {
			if (!running) {
				return;
			}
			running = false;

			_updateSubmittingForms(formId, false);
		}
	};
}
